import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { MixCard } from "./MixCard";
import { SectionTitle } from "./Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-colors ${active ? "bg-[#1FD1FF] text-black border-[#1FD1FF]" : "border-white/15 text-[#A8B3C4] hover:border-white/50 hover:text-white"}`}>
    {children}
  </button>
);

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const MixesSection = ({ onLoaded }) => {
  const { withOverrides } = usePlayer();
  const { user } = useAuth();
  const { t } = useT();
  const [mixes, setMixes] = useState([]);
  const [meta, setMeta] = useState({ genres: [], tags: [] });
  const [genre, setGenre] = useState("");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/mixes/meta").then((r) => setMeta({ genres: r.data?.genres ?? [], tags: r.data?.tags ?? [] })).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/mixes", { params: { genre: genre || undefined, tag: tag || undefined, q: q || undefined, sort } })
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : [];
          setMixes(list);
          if (!genre && !tag && !q) onLoaded?.(list);
        })
        .catch(() => setMixes([]));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genre, tag, q, sort, user]);

  const list = mixes.map(withOverrides);

  return (
    <section id="mixes" data-testid="mixes-section" className="max-w-[1500px] mx-auto px-4 lg:px-10 py-24 lg:py-36">
      <SectionTitle index="01" kicker={t("mixes.kicker")} title={<>{t("mixes.title")}<span className="text-[#1FD1FF]">.</span></>}>
        <div className="flex items-center gap-3 w-full lg:w-auto">
          <div className="relative flex-1 lg:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A8B3C4]" />
            <input data-testid="mix-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("mixes.search")}
              className="w-full bg-transparent border border-white/15 pl-10 pr-3 py-2.5 font-mono text-sm focus:outline-none focus:border-[#1FD1FF]" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="mix-sort-select" className="w-40 rounded-none font-mono text-xs uppercase"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["newest", "plays", "downloads", "likes"].map((s) => <SelectItem key={s} value={s}>{t(`sort.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </SectionTitle>

      <div className="flex flex-wrap gap-2 mb-4">
        <Chip testid="genre-filter-all" active={!genre} onClick={() => setGenre("")}>{t("mixes.all")}</Chip>
        {meta.genres.map((g) => <Chip key={g} testid={`genre-filter-${slug(g)}`} active={genre === g} onClick={() => setGenre(g)}>{g}</Chip>)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2 mb-14 font-mono text-xs">
        {meta.tags.map((tg) => (
          <button key={tg} data-testid={`tag-filter-${slug(tg)}`} onClick={() => setTag(tag === tg ? "" : tg)}
            className={`transition-colors ${tag === tg ? "text-[#1FD1FF]" : "text-[#A8B3C4] hover:text-white"}`}>#{tg}</button>
        ))}
      </div>

      {list.length === 0 ? (
        <p data-testid="mixes-empty" className="font-mono text-sm text-[#A8B3C4] py-20 text-center border border-dashed border-white/10">{t("mixes.empty")}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {list.map((m, i) => <MixCard key={m.id} mix={m} idx={i + 1} list={list} />)}
        </div>
      )}
    </section>
  );
};
